import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { authAPI } from '../lib/api';
import { setAccessToken, setAuthContext } from '../lib/api/utils';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessTokenState] = useState(null);
  
  // Ref to track if refresh is in progress
  const refreshingRef = useRef(false);
  const refreshPromiseRef = useRef(null);

  // Helper function to completely clear all auth state and storage
  const clearAllAuthState = (reason = 'unknown') => {
    // Clear React state FIRST
    setAccessTokenState(null);
    setAccessToken(null); // Clear API client token
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear ALL storage - do this AFTER state updates to avoid race conditions
    localStorage.clear(); // Nuclear option - clear everything
    sessionStorage.clear();
      
  };

  // Store access token in memory and update API headers
  const updateAccessToken = (token) => {
    setAccessTokenState(token);
    setAccessToken(token); // Update API client
    if (token) {
      // Update API client with new token
      localStorage.setItem('hasAuth', 'true'); // Flag to indicate auth state
    } else {
      localStorage.removeItem('hasAuth');
    }
  };

  // Refresh access token using refresh token (in HTTP-only cookie)
  const refreshAccessToken = async () => {
    // If refresh is already in progress, return the existing promise
    if (refreshingRef.current && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshingRef.current = true;
    
    const refreshPromise = authAPI.refresh()
      .then(response => {
        const { accessToken: newAccessToken, user: userData, hoa } = response;
        updateAccessToken(newAccessToken);
        setUser({ ...userData, hoa: hoa || null });
        setIsAuthenticated(true);
        return newAccessToken;
      })
      .catch(error => {
        console.log('Token refresh failed:', error.message);
        // Clear all auth state and storage
        clearAllAuthState('Refresh failed');
        throw error;
      })
      .finally(() => {
        refreshingRef.current = false;
        refreshPromiseRef.current = null;
      });

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  };

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have auth state indicator
        const hasAuth = localStorage.getItem('hasAuth');
        
        if (hasAuth) {
          // Try to refresh token to get current access token
          await refreshAccessToken();
        } 
      } catch (error) {
        // Clear all auth state and storage
        clearAllAuthState('Initialization failed');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up auth context for API client
  useEffect(() => {
    setAuthContext({
      refreshAccessToken,
      logout
    });
  }, []);

  // Set up automatic token refresh
  useEffect(() => {
    if (!accessToken || !isAuthenticated) return;

    // Parse token to get expiry time
    try {
      const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiryTime = tokenPayload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;
      
      // Refresh token 5 minutes before it expires
      const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 60 * 1000); // At least 1 minute
      
      const refreshTimer = setTimeout(() => {
        refreshAccessToken().catch(error => {
          console.log('Automatic token refresh failed:', error.message);
        });
      }, refreshTime);

      return () => clearTimeout(refreshTimer);
    } catch (error) {
      console.error('Error parsing access token:', error);
    }
  }, [accessToken, isAuthenticated]);

  const loginWithSession = (newAccessToken, userData) => {
    updateAccessToken(newAccessToken);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { accessToken: newAccessToken, user: userData, hoa } = response;
      
      updateAccessToken(newAccessToken);
      setUser({ ...userData, hoa: hoa || null });
      setIsAuthenticated(true);
      
      return { success: true, user: { ...userData, hoa: hoa || null } };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to revoke refresh token
      if (isAuthenticated) {
        await authAPI.logout();
      }
    } catch (error) {
      console.log('Logout API call failed:', error.message);
    } finally {
      // Clear all auth state and storage
      clearAllAuthState('Logout');
    }
  };

  // Get current access token (for API calls)
  const getAccessToken = () => {
    return accessToken;
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginWithSession,
    logout,
    getAccessToken,
    refreshAccessToken,
    hasRole,
    hasAnyRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 